import React, { useState } from 'react';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import Avatar from '@mui/joy/Avatar';
import Sheet from '@mui/joy/Sheet';
import IconButton from '@mui/joy/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationDialog from './NotificationDialog';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';

function getNotificationIcon(type) {
  switch(type) {
    case 'LOW_STOCK': return '⚠️';
    case 'ORDER_PROCESSED': return '📦';
    case 'SYSTEM_ALERT': return '🚨';
    default: return '📝';
  }
}

export default function NotificationDropdown() {
  const { notifications, readOne, clearOne } = useNotifications();
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await readOne(notification.notificationId);
      // Open detailed view
      setSelectedNotification(notification);
      setIsOpen(false);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Still open the notification even if marking as read fails
      setSelectedNotification(notification);
      setIsOpen(false);
    }
  };

  const handleCloseNotificationDialog = () => {
    setSelectedNotification(null);
  };

  return (
    <>
      <Dropdown open={isOpen} onOpenChange={(event, open) => setIsOpen(open)}>
        <MenuButton 
          slots={{ root: IconButton }}
          slotProps={{ 
            root: { 
              variant: 'soft',
              sx: {
                borderRadius: 'lg',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                border: 'none',
                position: 'relative',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)'
                }
              }
            } 
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <NotificationsRoundedIcon />
            {notifications.filter(n => !n.read).length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#ff9800',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6rem',
                  fontWeight: 'bold'
                }}
              >
                {notifications.filter(n => !n.read).length}
              </Box>
            )}
          </Box>
        </MenuButton>
        <Menu
          variant="soft"
          sx={{
            minWidth: 350,
            maxWidth: 400,
            maxHeight: 500,
            overflow: 'auto',
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 'lg',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            p: 0
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            position: 'relative',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography level="title-md" sx={{ 
              color: 'white', 
              textAlign: 'center', 
              width: '100%' 
            }}>
              Notifications
            </Typography>
            <IconButton 
              size="sm" 
              variant="soft" 
              onClick={() => setIsOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Notifications List */}
          {notifications.length === 0 ? (
            <Sheet 
              variant="soft" 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                border: '1px dashed rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography level="h4" sx={{ mb: 2 }}>
                No Notifications
              </Typography>
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                You're all caught up! No new notifications.
              </Typography>
            </Sheet>
          ) : (
            <List>
              {notifications.map((notification) => (
                <ListItem key={notification.notificationId}>
                  <ListItemButton 
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      bgcolor: notification.read ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 'md',
                      position: 'relative',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)'
                      }
                    }}
                  >
                    <Avatar 
                      variant="soft" 
                      src={notification.imageUrl}
                      sx={{ 
                        width: 48, 
                        height: 48,
                        border: '2px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      {getNotificationIcon(notification.itemType)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="title-sm" sx={{ color: 'white' }}>
                        {notification.name}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {notification.message}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#ff9800',
                          position: 'absolute',
                          top: 8,
                          right: 8
                        }}
                      />
                    )}
                    <IconButton
                      size="sm"
                      variant="soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearOne(notification.notificationId);
                      }}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'danger.500',
                          bgcolor: 'rgba(244, 67, 54, 0.1)'
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Menu>
      </Dropdown>

      {/* Notification Details Modal */}
      <NotificationDialog 
        open={!!selectedNotification} 
        onClose={handleCloseNotificationDialog} 
        notification={selectedNotification} 
      />
    </>
  );
} 